import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { PageWrapper } from '../components/ui/PageElements';
import PageMeta from '../components/PageMeta';

// Animations
const glitch = keyframes`
    0% { transform: translate(0); }
    20% { transform: translate(-5px, 5px); }
    40% { transform: translate(5px, -5px); }
    60% { transform: translate(-3px, -2px); }
    80% { transform: translate(5px, 5px); }
    100% { transform: translate(0); }
`;

const pulse = keyframes`
    0% { opacity: 0.7; }
    50% { opacity: 1; }
    100% { opacity: 0.7; }
`;

// Styled Components with transient props (using $prefix)
const Error404Container = styled.div`
    position: relative;
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const ContentWrapper = styled.div.attrs(props => ({
    style: {
        transform: `scale(${1 + props.$dimensionShift * 0.05})`,
        filter: `blur(${props.$dimensionShift * 1.5}px)`
    }
}))`
    position: relative;
    z-index: 10;
    padding: 40px;
    max-width: 800px;
    transition: transform 2s cubic-bezier(0.2, 0.8, 0.2, 1), filter 2s ease-out;
`;

const ErrorTitle = styled.h1.attrs(props => ({
    style: {
        textShadow: `0 0 ${15 + props.$dimensionShift * 20}px rgba(255, 51, 153, ${0.7 + props.$dimensionShift * 0.3})`
    }
}))`
    font-size: 8rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 2rem;
    position: relative;
    letter-spacing: -2px;
    animation: ${props => props.$glitchLevel > 0.5 ? glitch : 'none'} 0.5s ease-in-out infinite alternate;

    &::before, &::after {
        content: "404";
        position: absolute;
        top: 0;
        left: 0;
        opacity: ${props => props.$glitchLevel * 0.5};
        z-index: -1;
        pointer-events: none;
    }

    &::before {
        color: #00ffcc;
        transform: translateX(-${props => props.$glitchLevel * 10}px);
        clip-path: polygon(0 0, 100% 0, 100% 45%, 0 45%);
    }

    &::after {
        color: #ff00aa;
        transform: translateX(${props => props.$glitchLevel * 10}px);
        clip-path: polygon(0 55%, 100% 55%, 100% 100%, 0 100%);
    }
`;

const ErrorMessage = styled.p.attrs(props => ({
    style: {
        opacity: 1 - props.$dimensionShift * 0.4,
        transform: `translateY(${props.$dimensionShift * 10}px)`
    }
}))`
    font-size: 1.5rem;
    color: white;
    max-width: 100%;
    text-align: center;
    margin-bottom: 2rem;
    transition: opacity 1.5s ease-out, transform 1.5s ease-out;
`;

const Status = styled.div.attrs(props => ({
    style: {
        transform: `translateY(${props.$dimensionShift * 5}px)`,
        opacity: 1 - props.$dimensionShift * 0.2
    }
}))`
    font-family: inherit;
    font-size: 1rem;
    color: ${props => props.$error ? '#ff3399' : '#00ffcc'};
    margin-bottom: 2rem;
    border: 1px solid var(--primary-color);
    padding: 10px 20px;
    background-color: rgba(0, 0, 0, 0.5);
    transition: transform 1.5s ease-out, opacity 1.5s ease-out;
`;

const ReturnButton = styled.button.attrs(props => ({
    style: {
        transform: `scale(${1 + props.$dimensionShift * 0.1})`,
        boxShadow: `0 0 ${props.$dimensionShift * 15}px rgba(255, 51, 153, 0.3)`
    }
}))`
    background: transparent;
    border: 1px solid var(--primary-color);
    color: white;
    padding: 12px 24px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    z-index: 1;
    margin-top: 20px;
    font-family: inherit;
    text-transform: uppercase;
    animation: ${pulse} 2s infinite ease-in-out;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: var(--primary-color);
        transition: transform 0.3s ease;
        z-index: -1;
    }

    &:hover::before {
        transform: translateX(100%);
    }

    &:hover {
        color: black;
    }
`;

const GlitchOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
        90deg,
        rgba(255, 51, 153, 0) 0px,
        rgba(255, 51, 153, 0.03) 1px,
        rgba(255, 51, 153, 0) 2px
    );
    pointer-events: none;
    opacity: ${props => props.$glitchLevel * 0.7};
    z-index: 5;
    transition: opacity 0.5s ease;
    mix-blend-mode: overlay;
`;

const NotFound = () => {
    const [dimensionShift, setDimensionShift] = useState(0);
    const [glitchLevel, setGlitchLevel] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");
    const [voronoiMessage, setVoronoiMessage] = useState("");

    // Update shader uniforms function remains the same
    const updateShaderUniforms = () => {
        if (window.shaderUniforms) {
            window.shaderUniforms.uGlitchIntensity.value = glitchLevel;

            // Start Voronoi transition as soon as glitch level reaches 0.5
            if (glitchLevel > 0.5 && window.shaderUniforms.uVoronoiTransition) {
                window.shaderUniforms.uVoronoiTransition.value = Math.min(1.0, (glitchLevel - 0.5) * 2);
            }
        }
    };

    // Animation sequence remains largely the same but without the timer for the first voronoi message
    useEffect(() => {
        // Set up animation timing
        const timers = [];

        // Start with glitching
        const glitchInterval = setInterval(() => {
            setGlitchLevel(prev => {
                const next = Math.min(prev + 0.05, 1.0);
                setStatusMessage(`System error detected... Glitch level: ${Math.round(next * 100)}%`);
                updateShaderUniforms();
                return next;
            });
        }, 200);
        timers.push(glitchInterval);

        // After 3 seconds, start visual effects
        const visualTimer = setTimeout(() => {
            setStatusMessage("WARNING: System stability compromised");

            const visualInterval = setInterval(() => {
                setDimensionShift(prev => {
                    const next = Math.min(prev + 0.02, 1.0);
                    updateShaderUniforms();
                    return next;
                });
            }, 100);
            timers.push(visualInterval);
        }, 0);
        timers.push(visualTimer);

        // At 7 seconds, update Voronoi status
        const voronoiProgressTimer = setTimeout(() => {
            setVoronoiMessage("GRID MUTATION: Voronoi patterns emerging");
        }, 2500);
        timers.push(voronoiProgressTimer);

        // Final message
        const finalTimer = setTimeout(() => {
            setStatusMessage("System reconstruction failed. Access denied.");
            setVoronoiMessage("GRID COLLAPSE: Reality fracture complete");
        }, 6000);
        timers.push(finalTimer);

        // Cleanup all timers
        return () => {
            timers.forEach(timer => {
                if (typeof timer === 'number') {
                    clearTimeout(timer);
                } else {
                    clearInterval(timer);
                }
            });

            // Reset shader uniforms when component unmounts
            if (window.shaderUniforms) {
                window.shaderUniforms.uGlitchIntensity.value = 0.0;
            }
        };
    }, []);

    return (
        <PageWrapper>
            <PageMeta
                title="404 - Page Not Found"
                description="The page you're looking for doesn't exist or has been moved."
            />
            <Error404Container >
                {glitchLevel > 0 && <GlitchOverlay $glitchLevel={glitchLevel} />}

                <ContentWrapper $dimensionShift={dimensionShift}>
                    <ErrorTitle
                        $glitchLevel={glitchLevel}
                        $dimensionShift={dimensionShift}
                    >
                        404
                    </ErrorTitle>

                    <ErrorMessage $dimensionShift={dimensionShift}>
                        System corruption detected: data integrity compromised
                    </ErrorMessage>

                    <Status
                        $dimensionShift={dimensionShift}
                        $error={glitchLevel > 0.7}
                    >
                        {statusMessage || "Analyzing error..."}
                    </Status>

                    {/* Status for Voronoi transition */}
                    {voronoiMessage && (
                        <Status
                            $dimensionShift={dimensionShift * 1.2}
                            $error={true}
                            style={{ color: `rgba(0, 255, 204, ${Math.min(1, dimensionShift + 0.2)})` }}
                        >
                            {voronoiMessage}
                        </Status>
                    )}

                    <ErrorMessage $dimensionShift={dimensionShift}>
                        The digital plane you were looking for exists in another system
                    </ErrorMessage>

                    <ReturnButton
                        onClick={() => window.location.href = "/"}
                        $dimensionShift={dimensionShift}
                    >
                        RETURN_TO_REALITY.exe
                    </ReturnButton>
                </ContentWrapper>
            </Error404Container>
        </PageWrapper>
    );
};

export default NotFound;